import { IcoRuzukuLogo } from '@components/icons';
import { accentGradient, createBoxShadow } from 'shared/tenant-style-utils';
import { Brand } from './load';
import { useState } from 'preact/hooks';
import { StyledBrandButton } from '@course/components/styled-brand-button';
import { EditMode } from './types';
import { SettingsPanel } from './settings-panel';

function LoginForm({ brand, setEditMode }: { brand: Brand; setEditMode(mode: EditMode): void }) {
  return (
    <div
      class={`relative flex flex-col gap-6 cursor-pointer bg-white`}
      style={{
        borderRadius: brand.containerBorderRadius,
        padding: brand.containerPadding,
        boxShadow: createBoxShadow({ ...brand, shadowValue: brand.containerShadow }),
      }}
      onClick={(e) => {
        e.stopPropagation();
        setEditMode('container');
      }}
    >
      <div
        class="flex items-center justify-center cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setEditMode('logo');
        }}
      >
        <div class="inline-flex items-center justify-center px-8 relative focus:outline-hidden focus:ring-2 focus:ring-indigo-600 rounded-xs cursor-pointer">
          <span class="inline-block h-16">
            {brand.iconUrl ? (
              <img src={brand.iconUrl} class="h-full" />
            ) : (
              <IcoRuzukuLogo class="h-full w-auto" />
            )}
          </span>
        </div>
      </div>
      {brand.showName && (
        <span
          class="text-indigo-700 text-2xl px-12 flex justify-center"
          style={{ color: brand.logoColor }}
        >
          {brand.name}
        </span>
      )}
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label class="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            disabled
            class="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
            placeholder="you@example.com"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="block text-sm font-medium text-gray-700">Password</label>
          <input
            type="password"
            disabled
            class="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
            placeholder="••••••••"
          />
        </div>
      </div>
      <div
        class="flex flex-col text-center justify-center cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setEditMode('buttons');
        }}
      >
        <StyledBrandButton type="button" brand={brand}>
          Example primary action
        </StyledBrandButton>
        <a class="block mt-4" style={{ color: brand.primaryActionColor }}>
          Example link
        </a>
      </div>
    </div>
  );
}

function PreviewContent({
  brand,
  setEditMode,
}: {
  brand: Brand;
  setEditMode(mode: EditMode): void;
}) {
  if (brand.loginLayout === 'split') {
    return (
      <div
        class="flex-1 rounded-lg flex items-stretch h-[600px] cursor-pointer overflow-hidden"
        onClick={() => setEditMode('background')}
      >
        {/* Left side - Form */}
        <div
          class="w-1/2 flex items-center justify-center p-8 bg-white"
          onClick={(e) => {
            e.stopPropagation();
            setEditMode('container');
          }}
        >
          <LoginForm brand={brand} setEditMode={setEditMode} />
        </div>

        {/* Right side - Image/Color - Using the same background as the centered layout */}
        <div
          class="w-1/2 relative flex items-center justify-center overflow-hidden"
          onClick={(e) => {
            e.stopPropagation();
            setEditMode('background');
          }}
          style={{
            backgroundColor: brand.backgroundColor,
            backgroundImage: brand.loginBgUrl ? `url(${brand.loginBgUrl})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      </div>
    );
  }

  return (
    <div
      class="flex-1 p-10 py-40 rounded-lg flex items-center justify-center relative bg-gray-50 bg-cover cursor-pointer"
      style={{
        backgroundImage: brand.loginBgUrl ? `url(${brand.loginBgUrl})` : undefined,
        backgroundColor: brand.backgroundColor,
      }}
      onClick={() => setEditMode('background')}
    >
      <div class="relative py-3 my-4 w-lg max-w-full">
        {brand.enableGradient && (
          <div
            class="absolute inset-0 bg-linear-to-r from-indigo-400 to-blue-400 shadow-lg transform -skew-y-6 rounded-sm mx-2.5 cursor-pointer"
            style={{
              backgroundImage: accentGradient(brand),
            }}
            onClick={(e) => {
              e.stopPropagation();
              setEditMode('gradient');
            }}
          ></div>
        )}
        <LoginForm brand={brand} setEditMode={setEditMode} />
      </div>
    </div>
  );
}

export function LoginPreview({
  brand,
  setBrand,
}: {
  brand: Brand;
  setBrand: (fn: (b: Brand) => Brand) => void;
}) {
  const [editMode, setEditMode] = useState<EditMode | undefined>();

  const updateSetting = <K extends keyof Brand>(k: K, v: Brand[K]) => {
    setBrand((b) => ({ ...b, [k]: v }));
  };

  return (
    <div class="flex gap-6">
      <div class="w-80 p-4">
        <SettingsPanel
          editMode={editMode}
          setEditMode={setEditMode}
          brand={brand}
          setBrand={setBrand}
        />
      </div>
      <div class="flex-1 relative">
        <PreviewContent brand={brand} setEditMode={setEditMode} />

        <div class="absolute top-2 right-2 flex gap-2 bg-white rounded-md shadow-md p-1">
          <button
            type="button"
            class={`p-2 rounded-md transition-all ${
              brand.loginLayout === 'centered'
                ? 'bg-indigo-100 text-indigo-400'
                : 'text-gray-400 hover:bg-gray-100'
            }`}
            onClick={() => updateSetting('loginLayout', 'centered')}
            title="Centered Layout"
          >
            <span class="flex items-center justify-center p-1 rounded-md size-8 bg-current">
              <span class="flex bg-white rounded-xs size-3"></span>
            </span>
          </button>
          <button
            type="button"
            class={`p-2 rounded-md transition-all ${
              brand.loginLayout === 'split'
                ? 'bg-indigo-100 text-indigo-400'
                : 'text-gray-400 hover:bg-gray-100'
            }`}
            onClick={() => updateSetting('loginLayout', 'split')}
            title="2 Column Layout"
          >
            <span class="flex p-1 rounded-md size-8 bg-current">
              <span class="flex bg-white rounded-xs w-3"></span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
